import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { TAB_ID, useFocus } from '../../app/focus';
import { toast } from '../../components/toasts';
import { Badge, Button, ErrorText, Field, Input, Modal, Spinner, Textarea } from '../../components/ui';
import { ApiError, get, post, put } from '../../lib/api';
import { contactLabel, dateTimeOf, formatPhone } from '../../lib/format';
import { ticketLabel } from '../../lib/queries';
import { AttendanceItem, ContactView, ExternalService, FocusResult, TicketSummary } from '../../lib/types';
import { Customer, CustomerPicker, decisionBody, decisionError, TicketChoice, TicketChoiceValue } from './TicketFields';

interface AgideskContactOption {
  agideskContactId: string;
  name: string;
  email: string | null;
  phone: string;
  customers: Array<{ id: string; name: string }>;
  platformContactId: string | null;
}

interface StartContext {
  contact: ContactView;
  activeAttendance: AttendanceItem | null;
  previousAttendances: AttendanceItem[];
  openTickets: TicketSummary[] | null;
  services: ExternalService[] | null;
  agideskAvailable: boolean;
}

/**
 * O atendente inicia o contato com um cliente JÁ CADASTRADO (não há como digitar um número avulso):
 * escolhe o contato, retoma um chamado aberto ou abre uma nova situação e envia a primeira mensagem.
 */
export function StartConversationModal({ onClose, onOpen }: { onClose: () => void; onOpen: (attendanceId: string) => void }) {
  const qc = useQueryClient();
  const { expect, adopt } = useFocus();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [contactId, setContactId] = useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [choice, setChoice] = useState<TicketChoiceValue>({ mode: 'deferred' });
  const [text, setText] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  const results = useQuery({
    queryKey: ['contact-search', debounced],
    queryFn: () => get<ContactView[]>(`/contacts?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 2 && !contactId,
  });
  const agidesk = useQuery({
    queryKey: ['agidesk-contact-search', debounced],
    queryFn: () => get<AgideskContactOption[]>(`/agidesk/contacts?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 2 && !contactId,
    retry: false,
  });
  const [importing, setImporting] = useState<string | null>(null);
  const pickAgidesk = async (o: AgideskContactOption) => {
    if (o.platformContactId) return setContactId(o.platformContactId);
    setImporting(o.agideskContactId);
    setError(null);
    try {
      const contact = await post<ContactView>(`/contacts/import-agidesk/${o.agideskContactId}`);
      setContactId(contact.id);
    } catch (err) {
      setError(err);
    } finally {
      setImporting(null);
    }
  };
  // Contatos do Agidesk que ainda não aparecem na lista da plataforma.
  const platformIds = new Set((results.data ?? []).map((c) => c.id));
  const agideskOnly = (agidesk.data ?? []).filter((o) => !o.platformContactId || !platformIds.has(o.platformContactId));

  const ctx = useQuery({
    queryKey: ['start-context', contactId],
    queryFn: () => get<StartContext>(`/outbound/context?contactId=${contactId}`),
    enabled: Boolean(contactId),
    staleTime: 0,
  });

  useEffect(() => {
    const d = ctx.data;
    if (!d) return;
    setCustomer(d.contact.agidesk.customer);
    // Com chamado aberto, a sugestão é retomar; senão, nova situação.
    setChoice(d.openTickets?.length ? { mode: 'reuse', ticketId: d.openTickets[0].id } : { mode: 'create', serviceId: '', title: '' });
  }, [ctx.data]);

  const d = ctx.data;
  const start = async () => {
    const invalid = decisionError(choice, customer) ?? (!text.trim() ? 'Escreva a primeira mensagem para o cliente' : null);
    if (invalid) return setError(new Error(invalid));
    setSaving(true);
    setError(null);
    try {
      if ((customer?.id ?? null) !== (d!.contact.agidesk.customer?.id ?? null)) await put(`/contacts/${d!.contact.id}/customer`, { customerId: customer?.id ?? null });
      expect('*');
      const res = await post<{ attendanceId: string; focus: FocusResult }>('/conversations/start', {
        contactId: d!.contact.id,
        text: text.trim(),
        ticket: decisionBody(choice),
        clientTabId: TAB_ID,
      });
      adopt(res.focus);
      void qc.invalidateQueries({ queryKey: ['attendances'] });
      toast(choice.mode === 'create' ? 'Conversa iniciada. O chamado está sendo aberto no Agidesk.' : 'Conversa iniciada', 'ok');
      onOpen(res.attendanceId);
      onClose();
    } catch (err) {
      // Outro atendimento ficou ativo enquanto o modal estava aberto: oferece abrir.
      expect('');
      if (err instanceof ApiError && err.code === 'attendance.already_active') void ctx.refetch();
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const active = d?.activeAttendance;
  return (
    <Modal
      open
      title="Nova conversa com cliente cadastrado"
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          {d && !active && (
            <Button variant="primary" onClick={() => void start()} loading={saving}>
              Iniciar conversa
            </Button>
          )}
        </>
      }
    >
      {!contactId && (
        <>
          <p className="mb-3 text-sm text-fg-soft">Só é possível iniciar conversa com contatos cadastrados: os que já falaram com a empresa pelo WhatsApp e os do Agidesk que têm telefone no cadastro.</p>
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-fg-soft" />
            <Input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar contato: nome, telefone, e-mail ou cliente" className="pl-8" />
          </div>
          {(results.isFetching || agidesk.isFetching) && <Spinner className="mt-3 size-4" />}
          {results.data && (
            <div className="mt-2 max-h-72 overflow-y-auto rounded-md border border-border">
              <div className="bg-muted px-3 py-1 text-[11px] font-semibold tracking-wide text-fg-soft uppercase">Na plataforma</div>
              {results.data.length === 0 && <div className="px-3 py-2 text-sm text-fg-soft">Nenhum contato encontrado.</div>}
              {results.data.map((c) => (
                <button key={c.id} className="flex w-full items-center gap-3 border-b border-border px-3 py-2 text-left last:border-0 hover:bg-muted" onClick={() => setContactId(c.id)}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{c.name ?? formatPhone(c.phones[0])}</span>
                    <span className="block text-xs text-fg-soft">{c.phones.map(formatPhone).join(', ')}</span>
                  </span>
                  {c.agidesk.customer ? (
                    <Badge tone="accent">
                      <Building2 className="size-3" /> {c.agidesk.customer.name}
                    </Badge>
                  ) : (
                    <Badge tone="warn">sem cliente</Badge>
                  )}
                </button>
              ))}
              <div className="bg-muted px-3 py-1 text-[11px] font-semibold tracking-wide text-fg-soft uppercase">Cadastrados no Agidesk (com telefone)</div>
              {agidesk.isError && <div className="px-3 py-2 text-sm text-warn">Agidesk indisponível agora.</div>}
              {agidesk.data && agideskOnly.length === 0 && <div className="px-3 py-2 text-sm text-fg-soft">Nenhum outro contato no Agidesk.</div>}
              {agideskOnly.map((o) => (
                <button
                  key={o.agideskContactId}
                  disabled={importing !== null}
                  className="flex w-full items-center gap-3 border-b border-border px-3 py-2 text-left last:border-0 hover:bg-muted disabled:opacity-60"
                  onClick={() => void pickAgidesk(o)}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{o.name}</span>
                    <span className="block text-xs text-fg-soft">
                      {formatPhone(o.phone)}
                      {o.email ? ` · ${o.email}` : ''}
                    </span>
                  </span>
                  {importing === o.agideskContactId ? (
                    <Spinner className="size-4" />
                  ) : o.customers[0] ? (
                    <Badge tone="accent">
                      <Building2 className="size-3" /> {o.customers[0].name}
                    </Badge>
                  ) : (
                    <Badge tone="warn">sem cliente</Badge>
                  )}
                </button>
              ))}
            </div>
          )}
          <ErrorText error={error} />
        </>
      )}

      {contactId && (
        <>
          {ctx.isLoading && <Spinner />}
          <ErrorText error={ctx.error} />
          {d && (
            <>
              <div className="mb-4 flex items-center justify-between rounded-md bg-muted px-3 py-2">
                <div>
                  <div className="font-medium">{contactLabel({ contactName: d.contact.name, contactPhone: d.contact.phones[0] ?? null })}</div>
                  <div className="text-xs text-fg-soft">{d.contact.phones.map(formatPhone).join(', ')}</div>
                </div>
                <button className="text-xs text-accent hover:underline" onClick={() => setContactId(null)}>
                  trocar contato
                </button>
              </div>

              {active ? (
                <div className="rounded-md bg-warn/10 px-3 py-3 text-sm">
                  <p className="mb-2 text-warn">
                    Este contato já tem um atendimento ativo ({active.status === 'na_fila' ? 'na fila' : active.operatorName ? `com ${active.operatorName}` : active.status}).
                    Abra esse atendimento em vez de iniciar outro.
                  </p>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      onOpen(active.id);
                      onClose();
                    }}
                  >
                    Abrir atendimento ativo
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="mb-2 text-sm font-semibold">Cliente (empresa)</h3>
                  <div className="mb-4">
                    <CustomerPicker contact={d.contact} value={customer} onChange={setCustomer} />
                  </div>
                  <h3 className="mb-1 text-sm font-semibold">Assunto</h3>
                  <p className="mb-2 text-xs text-fg-soft">Retome um chamado em aberto do cliente ou abra um novo para uma nova situação.</p>
                  <TicketChoice
                    value={choice}
                    onChange={setChoice}
                    openTickets={d.openTickets}
                    services={d.services}
                    agideskAvailable={d.agideskAvailable}
                    defaultTitle={`Contato ativo WhatsApp - ${contactLabel({ contactName: d.contact.name, contactPhone: d.contact.phones[0] ?? null })}`}
                  />
                  {d.previousAttendances.length > 0 && (
                    <p className="mt-3 text-xs text-fg-soft">
                      Últimos atendimentos:{' '}
                      {d.previousAttendances
                        .map((p) => `${p.finishedAt ? dateTimeOf(p.finishedAt) : ''}${p.ticketId ? ` (${ticketLabel(p)})` : ''}`)
                        .join(' · ')}
                    </p>
                  )}
                  <div className="mt-4">
                    <Field label="Primeira mensagem" hint="(vai com o seu nome)">
                      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={4096} placeholder="Ex.: Olá! Aqui é da equipe de suporte, estou entrando em contato sobre…" />
                    </Field>
                  </div>
                </>
              )}
              <ErrorText error={error} />
            </>
          )}
        </>
      )}
    </Modal>
  );
}
